import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { LayerInfo } from '@root/search/models/map.models';
import { AppState } from '@root/app-state.module';
import { select, Store } from '@ngrx/store';
import {
  selectMlsListingsLayers,
  selectSalesValuesLayers,
  selectSelectedBoundaries,
  selectSelectedDistressedPropertiesLayers,
  selectSelectedLocationInformationLayers,
  selectSelectedPropertyCharacteristicLayers
} from '@store/map-layers.selector';
import {
  SetPropertyCharacteristic,
  ToggleBoundary,
  ToggleDistressedProperty,
  ToggleLocationInformation,
  ToggleMlsListing,
  ToggleSalesValue
} from '@store/search-board/search-board.actions';
import { LayerId } from '@root/search/models/map.constants';
import {
  selectSidePanelState
} from '@store/search-board/search-board.selector';
import { SidePanelTab, SidePanelView } from '@store/search-board/search-board.model';
import { MixpanelService } from '@shared/services/mixpanel.service';
import { SearchMixpanelUtil } from '@root/search/search-mixpanel-util';

@Component({
  selector: 'rlst-layers-legend',
  templateUrl: './layers-legend.component.html',
  styleUrls: ['./layers-legend.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LayersLegendComponent implements OnInit {
  public boundaryLayers$: Observable<Array<LayerInfo>>;
  public distressedPropertiesLayers$: Observable<Array<LayerInfo>>;
  public salesValuesLayers$: Observable<Array<LayerInfo>>;
  public mlsListingsLayers$: Observable<Array<LayerInfo>>;
  public locationInformationLayers$: Observable<Array<LayerInfo>>;
  public propertyCharacteristics$: Observable<Array<LayerInfo>>;
  public hasSelectedLayers$: Observable<boolean>;

  private sidePanelTabOpened: SidePanelTab;
  private readonly searchMixpanelUtil: SearchMixpanelUtil = new SearchMixpanelUtil(this.store$);

  constructor(private readonly store$: Store<AppState>,
              private readonly mixpanelService: MixpanelService) {}

  ngOnInit(): void {
    this.boundaryLayers$ = this.store$.pipe(
      select(selectSelectedBoundaries),
      map((layers: Array<LayerInfo>) => {
        return layers.filter((layer: LayerInfo) => {
          return ![
            LayerId.FLOOD_ZONE_LAYER,
            LayerId.FAULT_LINE_LAYER,
            LayerId.PLANNED_LAND_USE_LAYER
          ].includes(layer.layerId);
        });
      })
    );
    this.distressedPropertiesLayers$ = this.store$.pipe(select(selectSelectedDistressedPropertiesLayers));
    this.locationInformationLayers$ = this.store$.pipe(select(selectSelectedLocationInformationLayers));
    this.propertyCharacteristics$ = this.store$.pipe(select(selectSelectedPropertyCharacteristicLayers));
    this.salesValuesLayers$ = this.store$.pipe(
      select(selectSalesValuesLayers),
      map((layers: Array<LayerInfo>) => {
        return layers.filter((layer: LayerInfo) => layer.isSelected);
      })
    );
    this.mlsListingsLayers$ = this.store$.pipe(
      select(selectMlsListingsLayers),
      map((layers: Array<LayerInfo>) => {
        return layers.filter((layer: LayerInfo) => layer.isSelected);
      })
    );

    this.hasSelectedLayers$ = combineLatest(
      [
        this.boundaryLayers$,
        this.distressedPropertiesLayers$,
        this.locationInformationLayers$,
        this.propertyCharacteristics$,
        this.salesValuesLayers$,
        this.mlsListingsLayers$
      ]
    ).pipe(
      map((layersList: Array<Array<LayerInfo>>) => {
        return layersList
          .some((layers: Array<LayerInfo>) => layers.length > 0);
      })
    );

    this.store$
      .pipe(
        select(selectSidePanelState)
      )
      .subscribe((sidePanelView: SidePanelView) => {
        this.sidePanelTabOpened = sidePanelView.sidePanelTabOpened;
      });
  }

  trackByLayerId(_: number, layer: LayerInfo): string {
    return layer.layerId;
  }

  closeBoundaryLayer(layer: LayerInfo): void {
    this.logMapBoundariesMixPanel('Search - Boundary Removed', layer.label);
    return this.store$.dispatch(new ToggleBoundary(layer));
  }

  closeDistressedPropertyLayer(layer: LayerInfo): void {
    return this.store$.dispatch(new ToggleDistressedProperty(layer));
  }

  closeSalesValuesLayer(layer: LayerInfo): void {
    return this.store$.dispatch(new ToggleSalesValue(layer));
  }

  closeMlsListingsLayer(layer: LayerInfo): void {
    return this.store$.dispatch(new ToggleMlsListing(layer));
  }

  closeLocationInformationLayer(layer: LayerInfo): void {
    return this.store$.dispatch(new ToggleLocationInformation(layer));
  }

  closePropertyCharacteristic(): void {
    return this.store$.dispatch(new SetPropertyCharacteristic(null));
  }

  private logMapBoundariesMixPanel(eventId: string, boundaryType: string) {
    this.searchMixpanelUtil.setupMixPanelItems(this.sidePanelTabOpened);

    this.mixpanelService.track(eventId, {
      countyList: this.searchMixpanelUtil.countyList,
      searchType: this.sidePanelTabOpened,
      isSavedSearch: (this.sidePanelTabOpened === 'quickSearch') ? false :
        this.searchMixpanelUtil.selectedSearch !== 'My Search Default',
      searchParameters: this.searchMixpanelUtil.searchFieldNames.join('; '),
      boundaryType: boundaryType
    });
  }
}
